/** @jsxImportSource @emotion/react */
import React from "react";
import { css, Interpolation, Theme } from "@emotion/react";
import Colors from "styles/colors";

export interface ToggleButtonProps {
  selected?: boolean;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  className?: string;
  css?: Interpolation<Theme>;
  children: React.ReactNode;
  "aria-label"?: string;
  value: string;
}

const sizeMap = {
  small: {
    fontSize: 13,
    padding: "3px 9px",
    size: 32,
    borderRadius: 6,
  },
  medium: {
    fontSize: 14,
    padding: "6px 16px",
    size: 40,
    borderRadius: 8,
  },
  large: {
    fontSize: 15,
    padding: "8px 22px",
    size: 48,
    borderRadius: 8,
  },
};

const baseStyle = ({
  selected,
  disabled,
  size,
}: {
  selected?: boolean;
  disabled?: boolean;
  size: "small" | "medium" | "large";
}) => {
  const s = sizeMap[size] || sizeMap.medium;

  return css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${Colors.background.MID_GRAY_MOUSEOVER}`,
    background: selected
      ? Colors.background.DARKEN_ALPHA_OVERLAY
      : Colors.background.WHITE,
    color: disabled
      ? Colors.background.MID_GRAY_MOUSEOVER
      : Colors.background.DARK_MOUSEOVER_GRAY,
    borderRadius: s.borderRadius,
    padding: 0, // Remove padding to ensure square
    fontSize: s.fontSize,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    transition: "background 0.2s, border 0.2s, color 0.2s",
    outline: "none",
    minWidth: s.size,
    minHeight: s.size,
    width: s.size,
    height: s.size,
    aspectRatio: "1 / 1",
    userSelect: "none",
    fontWeight: 500,
    letterSpacing: 0.5,
    ":hover":
      !disabled && !selected
        ? { background: Colors.background.LIGHT_MOUSEOVER_GRAY }
        : undefined,
    ":active": !disabled
      ? {
          background: selected
            ? Colors.background.LIGHT_MOUSEOVER_GRAY
            : Colors.background.WHITE,
        }
      : undefined,
    ":focus": {
      outline: "none",
      boxShadow: "none",
    },
  });
};

export const ToggleButton: React.FC<ToggleButtonProps> = ({
  selected = false,
  disabled = false,
  value,
  size = "medium",
  onClick,
  className,
  css: cssProp,
  children,
  "aria-label": ariaLabel,
}) => {
  // Detect if the child is an icon (FontAwesomeIcon or any React element)
  const isIconOnly =
    React.isValidElement(children) && typeof children.type === "function";
  const content =
    !isIconOnly && typeof children === "string" ? (
      <span style={{ textTransform: "uppercase", fontWeight: 700 }}>
        {children}
      </span>
    ) : (
      children
    );
  return (
    <button
      type="button"
      css={[
        baseStyle({ selected, disabled, size }),
        isIconOnly && {
          width: sizeMap[size].size,
          height: sizeMap[size].size,
          minWidth: sizeMap[size].size,
          minHeight: sizeMap[size].size,
          aspectRatio: "1 / 1",
        },
        !isIconOnly && {
          padding: sizeMap[size].padding,
          width: "auto",
          height: sizeMap[size].size,
          minWidth: sizeMap[size].size,
          minHeight: sizeMap[size].size,
          aspectRatio: "auto",
        },
        cssProp,
      ]}
      className={className}
      onClick={disabled ? undefined : onClick}
      aria-pressed={selected}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      disabled={disabled}
      value={value}
    >
      {content}
    </button>
  );
};

export default ToggleButton;
